import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { showAlert } from 'actions/app';
import moment from 'moment';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import qs from 'query-string';
import { filtersToParams } from 'selectors/app';
import API from 'services/API';
import { getDashboardStats } from 'services/bulkEmail/dashboard';
import strings from 'strings';
import Text from 'UI/components/atoms/Text';
import LineChart from 'UI/components/molecules/Dashboard/LineChart';
import StatsWrapper from 'UI/components/molecules/Dashboard/StatsWrapper';
import SummaryTable from 'UI/components/molecules/Dashboard/SummaryTable';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { backNavigateListConfig } from 'UI/constants/config';
import { DateFormats, PageTitles } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { dateFormatter, downloadFile, getRangePeriod } from 'UI/utils';

import { getScope } from '../helpers';

const FiltersGroups = [{ id: 'team', name: 'Team', filters: ['coach', 'recruiter'] }];
const {
  title: pageTitle,
  infoAlert,
  section: { name: strSectionName, titles }
} = strings.bulkEmailDashboard;

const getAlertCopy = date =>
  strings.formatString(infoAlert, {
    lastUpdate: dateFormatter(
      date,
      ({ MonthDayNumberYear, SimpleTime }) => `${MonthDayNumberYear} [at] <b>${SimpleTime}</b>`
    )
  });

const DashboardBulkEmail = () => {
  const dispatch = useDispatch();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const defaultPeriod = getRangePeriod([1, 'week']);

  const [selectedPeriod, setSelectedPeriod] = useState(defaultPeriod);
  const [filters, setFilters] = useState({});
  const [uiState, setUiState] = useState({
    isSideMenuOpen: false,
    fetching: false
  });
  const [dashboardData, setDashboardData] = useState({
    lastUpdate: '',
    sent: [],
    trend: {},
    stats: {
      open: [],
      total: []
    },
    activity: {
      withBulk: {
        columns: [],
        rows: []
      },
      withoutBulks: {
        columns: [],
        rows: []
      }
    }
  });

  const filtersParams = useMemo(() => filtersToParams(filters), [filters]);
  const isNewStatsFlagEnabled = useMemo(
    () => checkIfFeatureFlagEnabled(FeatureFlags.BulkEmailNewDashboardStats),
    [checkIfFeatureFlagEnabled]
  );

  const getEndpoint = useCallback(
    url =>
      qs.stringifyUrl({
        url,
        query: {
          startDate: moment(selectedPeriod.startDate).format(DateFormats.QueryFormat),
          endDate: moment(selectedPeriod.endDate).format(DateFormats.QueryFormat),
          ...filtersParams
        }
      }),
    [selectedPeriod, filtersParams]
  );

  const scope = getScope(filters);
  const activityUrl = getEndpoint(Endpoints.DashboardBulkEmailDownloadCSV);

  useEffect(() => {
    document.title = PageTitles.DashboardBulk;
  }, []);

  useEffect(() => {
    const setFetching = fetching =>
      setUiState(prev => ({
        ...prev,
        fetching
      }));
    setFetching(true);
    (async () => {
      const { alert, data, success } = await getDashboardStats({
        queryParams: {
          ...selectedPeriod,
          ...filtersParams
        }
      });
      if (success) {
        const {
          formattedBulkStats: stats,
          formattedSentBulks: sent,
          formattedTrendBulks: trend,
          formattedTableBulkSents: activity,
          latestCutoffDate: lastUpdate
        } = data;

        const areStatsOfTypeArray = Array.isArray(stats);
        const finalStats =
          (isNewStatsFlagEnabled && !areStatsOfTypeArray) || !areStatsOfTypeArray
            ? stats
            : {
                openRatio: stats.slice(0, 3),
                overallRatio: stats.slice(3, 6)
              };

        setDashboardData({
          lastUpdate,
          sent,
          trend,
          activity,
          stats: finalStats
        });
      } else {
        dispatch(showAlert(alert));
      }
      setFetching(false);
    })();
  }, [selectedPeriod, filtersParams, dispatch, isNewStatsFlagEnabled, getEndpoint]);

  const handlePeriodChange = period => setSelectedPeriod({ ...period });
  const handleFiltersReset = () => setFilters({});
  const handleFiltersChange = newFilters => setFilters(newFilters);
  const handleFiltersSearch = (_, newFilters) => setFilters(newFilters);
  const handleRowClick = filter => setFilters(prev => ({ ...prev, ...filter }));
  const handleMenuToggle = () =>
    setUiState(prev => ({ ...prev, isSideMenuOpen: !prev.isSideMenuOpen }));
  const handleDownloadAll = async ({ url, fileName }) => {
    const response = await API.get(`${url}&allRecruiters=true&format=csv`, {
      responseType: 'blob',
      apiVersion: 2
    });
    downloadFile(response.data, 'text/csv', `${fileName}-allRecruiters`);
  };

  const stats = {
    firstSection: [
      {
        data: dashboardData.sent,
        shouldShow: true,
        title: titles.totalSent
      },
      {
        data: dashboardData.stats.openRatio,
        shouldShow: true,
        title: titles.ratio
      },
      {
        data: dashboardData.stats.overallRatio,
        shouldShow: true,
        title: titles.totalStats
      },
      {
        data: dashboardData.stats?.directReplyRatio,
        shouldShow: isNewStatsFlagEnabled && dashboardData.stats?.directReplyRatio,
        title: titles.manualReplies
      },
      {
        data: dashboardData.stats?.automatedReplyRatio,
        shouldShow: isNewStatsFlagEnabled && dashboardData.stats?.automatedReplyRatio,
        title: titles.automaticReplies
      }
    ],
    secondSection: [
      {
        child: <LineChart data={dashboardData.trend} columns={[]} />,
        shouldShow: true,
        shouldShowFooter: true,
        title: titles.trends
      },
      {
        child: (
          <SummaryTable
            data={dashboardData.activity?.withBulk}
            url={activityUrl}
            fetching={uiState.fetching}
            downloadFileName={`Bulk-Overview-${scope.option2}`}
            section={strSectionName}
            onRowClick={handleRowClick}
            onDownloadAll={handleDownloadAll}
            showDownloadAllButton={!filters?.recruiter}
          />
        ),
        FooterProps: {
          bulksLastUpdate: dashboardData.lastUpdate,
          type: 'table'
        },
        shouldShow: true,
        shouldShowFooter: true,
        subheader: scope.option2,
        title: titles.withActivity,
        contentPadding: 'bottom'
      }
    ]
  };

  const statsWrapperCommonProps = {
    FooterProps: {
      bulksLastUpdate: dashboardData.lastUpdate
    },
    isLoading: uiState.fetching,
    scope,
    shouldSetMinHeight: true
  };

  return (
    <FiltersLayout
      title={pageTitle}
      section={strSectionName}
      isSideMenuOpen={uiState.isSideMenuOpen}
      defaultRange={selectedPeriod}
      onPeriodChange={handlePeriodChange}
      onFiltersReset={handleFiltersReset}
      onFiltersChange={handleFiltersChange}
      onSearch={handleFiltersSearch}
      onMenuToggle={handleMenuToggle}
      groups={FiltersGroups}
      disableTimeRange
      titleLabelProps={backNavigateListConfig}
      filters={filters}
      enableStore={false}
    >
      <Grid container spacing={3}>
        <StatsWrapper {...statsWrapperCommonProps} stats={stats.firstSection} />
        <Grid item xs={12}>
          <Alert severity="info">
            <Text text={getAlertCopy(dashboardData.lastUpdate)} />
          </Alert>
        </Grid>
        <StatsWrapper {...statsWrapperCommonProps} stats={stats.secondSection} />
      </Grid>
    </FiltersLayout>
  );
};

export default DashboardBulkEmail;
